import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatErr } from "@/lib/api";
import { ArrowLeft, MapPin, Printer, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import InvoicePrintHeader from "@/components/InvoicePrintHeader";
import DeliveryPhotos from "@/components/DeliveryPhotos";
import CardknoxPayDialog from "@/components/CardknoxPayDialog";
import { logError } from "@/lib/log";

export default function CustomerInvoiceDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [cof, setCof] = useState(null);              // card_on_file from /portal/me
  const [chargingSaved, setChargingSaved] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/invoices/${id}`);
    setInv(r.data);
    // Also fetch the portal /me so we can offer "Pay with card on file" when one exists.
    try {
      const me = await api.get("/portal/me");
      setCof((me.data?.customer || {}).card_on_file || null);
    } catch (e) { logError("Portal card-on-file load failed (non-fatal):", e); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const payWithCardOnFile = async () => {
    if (!cof?.token) return;
    if (!window.confirm(`Pay ${fmtMoney(inv.total)} with card ending in ${cof.last4 || "????"}?`)) return;
    setChargingSaved(true);
    try {
      const { data } = await api.post(`/payments/cardknox/charge-saved`, { invoice_id: inv.id });
      toast.success(`Paid · Ref ${data.ref_num || ""}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setChargingSaved(false); }
  };

  if (!inv) return <div className="overline">Loading…</div>;

  return (
    <div className="space-y-6 animate-fade-up">
      <Link to="/portal" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground no-print" data-testid="portal-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to portal
      </Link>

      <div className="border border-primary/25 bg-primary/[0.03] rounded-sm shadow-sm p-8 md:p-12 print-container print-shadow">
        <InvoicePrintHeader inv={inv} />
        <div className="flex justify-between items-start mb-8">
          <div>
            <p className="overline text-primary">Invoice</p>
            <h1 className="font-display text-4xl font-black tracking-tighter mt-2 mono-num text-primary">{inv.number}</h1>
            <p className="text-sm text-muted-foreground mt-2">Delivery {fmtDate(inv.delivery_date)}</p>
            {inv.pool_name && (
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {inv.pool_name} — {inv.pool_address}
              </p>
            )}
          </div>
          <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 ${inv.status === 'paid' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}`}>
            {inv.status}
          </span>
        </div>

        <div className="space-y-2 mb-8 bg-background border border-primary/20 rounded-sm overflow-hidden">
          <div className="grid grid-cols-12 overline text-primary py-2.5 px-4 bg-primary/10 border-b border-primary/20">
            <div className="col-span-6">Description</div>
            <div className="col-span-2 text-right">Qty</div>
            <div className="col-span-2 text-right">Rate</div>
            <div className="col-span-2 text-right">Amount</div>
          </div>
          {(inv.line_items && inv.line_items.length > 0 ? inv.line_items : [{
            description: "Bulk chlorine delivery",
            quantity: inv.gallons,
            unit_price: inv.price_per_gallon,
            amount: inv.subtotal,
          }]).map((li, i) => (
            <div key={`cli-${i}-${li.description}`} className="grid grid-cols-12 py-3 px-4 border-b border-primary/10 last:border-b-0">
              <div className="col-span-6">
                {li.description}
                {li.taxable === false && <span className="text-xs text-muted-foreground ml-2">(non-taxable)</span>}
              </div>
              <div className="col-span-2 text-right mono-num">{li.quantity}</div>
              <div className="col-span-2 text-right mono-num">{fmtMoney(li.unit_price)}</div>
              <div className="col-span-2 text-right mono-num">{fmtMoney(li.amount)}</div>
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <div className="w-80 bg-primary/10 border border-primary/30 rounded-sm p-5 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="mono-num">{fmtMoney(inv.subtotal)}</span></div>
            {inv.tax_exempt ? (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-2">
                  Tax
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-success/10 text-success text-[10px] font-bold uppercase tracking-wider" data-testid="customer-invoice-tax-exempt-badge">
                    Exempt{inv.tax_exempt_number ? ` · ${inv.tax_exempt_number}` : ""}
                  </span>
                </span>
                <span className="mono-num">{fmtMoney(0)}</span>
              </div>
            ) : (
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tax ({inv.tax_rate}%)</span><span className="mono-num">{fmtMoney(inv.tax)}</span></div>
            )}
            <div className="flex justify-between pt-3 border-t border-primary/30 items-baseline">
              <span className="font-bold text-primary uppercase tracking-wider text-xs">Amount due</span>
              <span className="font-display text-3xl font-black tracking-tighter mono-num text-primary">{fmtMoney(inv.total)}</span>
            </div>
          </div>
        </div>

        {inv.status === "unpaid" && (
          <div className="mt-10 pt-6 border-t border-border flex flex-col items-stretch md:items-end gap-3 no-print">
            <div className="flex items-center gap-3 flex-wrap justify-end">
              <Button onClick={() => window.print()} variant="outline" className="rounded-sm h-12 px-6" data-testid="print-invoice-button">
                <Printer className="w-4 h-4 mr-2" /> Print / PDF
              </Button>
              {cof?.token && (
                <Button
                  onClick={payWithCardOnFile}
                  disabled={chargingSaved}
                  className="rounded-sm h-12 px-6 font-semibold"
                  data-testid="pay-with-card-on-file-button"
                >
                  {chargingSaved
                    ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Charging…</>)
                    : (<><CreditCard className="w-4 h-4 mr-2" /> Pay {fmtMoney(inv.total)} with card ending {cof.last4 || "????"}</>)}
                </Button>
              )}
              <Button
                onClick={() => setPayOpen(true)}
                variant={cof?.token ? "outline" : "default"}
                className="rounded-sm h-12 px-6 font-semibold"
                data-testid="pay-invoice-button"
              >
                <CreditCard className="w-4 h-4 mr-2" /> {cof?.token ? "Use a different card" : `Pay ${fmtMoney(inv.total)}`}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Card data is sent directly to Cardknox — Catskills Pool Supplies never sees your card number.</p>
          </div>
        )}

        <CardknoxPayDialog
          open={payOpen}
          onClose={() => setPayOpen(false)}
          invoice={inv}
          onPaid={() => load()}
        />

        {inv.status === "paid" && (
          <div className="mt-10 pt-6 border-t border-border flex justify-end no-print">
            <Button onClick={() => window.print()} variant="outline" className="rounded-sm h-10 px-5" data-testid="print-invoice-button">
              <Printer className="w-4 h-4 mr-2" /> Print / PDF
            </Button>
          </div>
        )}

        <DeliveryPhotos photos={inv.delivery_photos} extraPhotos={inv.delivery_photo_data_urls} />
      </div>
    </div>
  );
}
